import { z } from 'zod';
import {
  baseDocSchema,
  currencyCodeSchema,
  localeSchema,
  objectIdSchema,
  priceCentsSchema,
} from './common.js';

export const orderStatusSchema = z.enum(['pending', 'paid', 'fulfilled', 'refunded', 'failed']);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const orderItemSchema = z.object({
  ticketTypeId: objectIdSchema,
  quantity: z.number().int().min(1),
  unitPriceCents: priceCentsSchema,
  ticketSnapshot: z.object({
    name: z.string(),
    kind: z.string(),
  }),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

export const utmSchema = z
  .object({
    source: z.string().max(120).optional(),
    medium: z.string().max(120).optional(),
    campaign: z.string().max(120).optional(),
    term: z.string().max(120).optional(),
    content: z.string().max(120).optional(),
  })
  .partial();
export type Utm = z.infer<typeof utmSchema>;

export const orderSchema = baseDocSchema.extend({
  eventId: objectIdSchema,
  orgId: objectIdSchema,
  buyer: z.object({
    email: z.string().email().toLowerCase(),
    name: z.string().min(1).max(120),
    phone: z.string().max(40).optional(),
    locale: localeSchema.default('es'),
    photoUrl: z.string().url().optional(),
  }),
  items: z.array(orderItemSchema).min(1),
  currency: currencyCodeSchema,
  subtotalCents: priceCentsSchema,
  serviceFeeCents: priceCentsSchema,
  totalCents: priceCentsSchema,
  status: orderStatusSchema.default('pending'),
  stripePaymentIntentId: z.string().nullable().default(null),
  stripeChargeId: z.string().nullable().default(null),
  utm: utmSchema.default({}),
  fulfilledAt: z.coerce.date().nullable().default(null),
  refundedAt: z.coerce.date().nullable().default(null),
  disputedAt: z.coerce.date().nullable().optional(),
  disputeStatus: z.string().optional(),
  fulfillmentHeldAt: z.coerce.date().nullable().optional(),
});
export type Order = z.infer<typeof orderSchema>;

export const createOrderInputSchema = z.object({
  eventId: objectIdSchema,
  buyer: z.object({
    email: z.string().trim().toLowerCase().email(),
    name: z.string().min(1).max(120),
    phone: z.string().max(40).optional(),
    locale: localeSchema.default('es'),
  }),
  items: z
    .array(
      z.object({
        ticketTypeId: objectIdSchema,
        quantity: z.number().int().min(1).max(100),
      }),
    )
    .min(1),
  utm: utmSchema.optional(),
});
export type CreateOrderInput = z.infer<typeof createOrderInputSchema>;
